import { AgreementStatus } from 'src/lib/fliko/helpers/user.helper';
import { CommunityTenancyData } from 'src/lib/fliko/models/agreements/communityTenancy.data';
import { ProtocolData } from 'src/lib/fliko/models/agreements/protocol.data';
import { TenancyAgreementData } from 'src/lib/fliko/models/agreements/tenancyAgr.data';
import { ResidentData, ResidentType } from 'src/lib/fliko/models/person/resident.data';
import { ApartmentData } from 'src/lib/fliko/models/units/apartment.data';
import { StorageData } from 'src/lib/fliko/models/units/storage.data';
import { RentalUnit, RentalUnitType } from 'src/lib/fliko/models/units/unit.data';
import { ParkingData } from 'src/lib/fliko/models/units/parking.data';
import { ShareData } from 'src/lib/fliko/models/users/share.data';
import { UserData } from 'src/lib/fliko/models/users/user.data';
import { TenancyDetails } from './users-agreement.data';
import { RentalUnitVM } from './users-view-models';

export interface UserPRSTabData {
  residents: Map<string, ResidentData>;
  agreements: TenancyAgreementData[];
  apartments: Map<string, ApartmentData>;
  parkingSpots: Map<string, ParkingData>;
  storages: Map<string, StorageData>;
  allUnits: RentalUnit[];
  protocols: ProtocolData[];
}

export interface UserCommunityTabData {
  residents: ResidentData[];
  shares: ShareData[];
  apartments: ApartmentData[];
  parkings: ParkingData[];
  storages: StorageData[];
  apBankAcc: Map<string, string>;
  parBankAcc: Map<string, string>;
  stoBankAcc: Map<string, string>;
  communityTenancy: CommunityTenancyData[];
  estateShareSum: number;
  usersMap: Map<string, UserData>;
}

export interface BasicResidentElement {
  id: string;
  resident: ResidentData;
  type: ResidentType;
}

export interface BasicUnitElement {
  id: string;
  unit: RentalUnit;
  type: RentalUnitType;
  landlords: ResidentData[];
}

export namespace UserCommList {
  export interface ResidentElement extends BasicResidentElement {
    creator: UserData | null;
    archiveType: ResidentType | null;
    apartments: ApartmentData[];
    parkings: ParkingData[];
    storages: StorageData[];
    archiveApartments: ApartmentData[];
    archiveParkings: ParkingData[];
    archiveStorages: StorageData[];
    shares: ShareData[] | null;
    displayShare: string | null;
  }

  export function emptyResidentElement(
    resident: ResidentData,
    usersMap: Map<string, UserData>,
    type: ResidentType
  ): UserCommList.ResidentElement {
    return {
      id: resident.id,
      resident,
      type,
      creator: usersMap.get(resident.creatorUserId ?? '') ?? null,
      archiveType: null,
      shares: null,
      displayShare: null,
      apartments: [],
      parkings: [],
      storages: [],
      archiveApartments: [],
      archiveParkings: [],
      archiveStorages: []
    };
  }

  export interface UnitElement extends BasicUnitElement {
    shares: ShareData[];
    displayShare: string;
  }

  export interface ApartmentElement extends UnitElement {
    unit: ApartmentData;
    type: RentalUnitType.APARTMENT;
    parkings: RentalUnitVM[];
    storages: RentalUnitVM[];
  }

  export interface ParkingStorageElement extends UnitElement {
    unit: ParkingData | StorageData;
    apartment?: ApartmentData;
  }
}

export namespace UserPRSList {
  export interface AgreementElement {
    id: string;
    agreement: TenancyAgreementData;
    status: AgreementStatus;
    apartments: RentalUnitVM[];
    landlords: ResidentData[];
    tenants: ResidentData[];
    parkingSpots: RentalUnitVM[];
    storages: RentalUnitVM[];
    protocols: ProtocolData[];
    date: { from: number; to: number | null };
  }

  export interface RentalUnitElement extends BasicUnitElement {
    agreement?: TenancyAgreementData;
    tenancyDetails?: TenancyDetails;
    tenants: ResidentData[];
    archivedAgreements: TenancyAgreementData[];
    futureAgreements: TenancyAgreementData[];
  }

  export interface ResidentElement extends BasicResidentElement {
    currentAgreements: TenancyAgreementData[];
    archivedAgreements: TenancyAgreementData[];
    futureAgreements: TenancyAgreementData[];
    apartments: RentalUnitVM[];
    parkingSpots: RentalUnitVM[];
    storages: RentalUnitVM[];
  }
}
